import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Video, ArrowLeft, LogOut, Loader2, Sparkles, Home } from 'lucide-react'; 
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const MyJobs: React.FC = () => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMyJobs = async () => {
      try {
        const res = await api.get('/jobs/my-jobs');
        setJobs(res.data);
      } catch (err) {
        console.error("Failed to fetch jobs");
      } finally {
        setLoading(false);
      }
    };
    fetchMyJobs();
  }, []);

  if (loading) return (
    <div className="h-screen w-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex flex-col items-center justify-center text-indigo-400 gap-4 relative">
      <Loader2 className="animate-spin" size={40} />
      <p className="font-black uppercase tracking-[0.3em] text-[10px]">{t('retrieving_requests', 'Retrieving Requests...')}</p>
    </div>
  );

  return (
    <div className="h-full w-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 flex flex-col overflow-hidden relative">
      
      <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
        <div className="max-w-5xl mx-auto w-full p-4 md:p-10 pb-32">
          
          <div className="flex justify-between items-center mb-8 md:mb-12">
            <button 
              onClick={() => navigate('/student/home')}
              className="flex items-center gap-2 text-slate-500 hover:text-white transition-all font-black uppercase text-[9px] md:text-xs tracking-widest bg-slate-900/50 px-4 py-2 rounded-xl border border-slate-800"
            >
              <ArrowLeft size={14} /> <span className="hidden xs:block">{t('dashboard', 'Dashboard')}</span>
            </button>
            
            <button 
              onClick={() => window.confirm(t('confirm_logout', "Logout?")) && logout()}
              className="flex items-center gap-2 text-red-500/80 hover:text-red-400 transition-all font-black uppercase text-[9px] md:text-xs tracking-widest bg-red-500/5 px-4 py-2 rounded-xl border border-red-500/10"
            >
              <LogOut size={14} /> <span className="hidden xs:block">{t('sign_out', 'Sign Out')}</span>
            </button>
          </div>

          <header className="mb-8 md:mb-10">
            <h2 className="text-3xl md:text-5xl font-black uppercase italic tracking-tighter text-white leading-none">
              {t('my_requests_title_1', 'My')} <span className="text-indigo-500">{t('my_requests_title_2', 'Requests')}</span>
            </h2>
            <p className="text-slate-500 font-bold uppercase text-[8px] md:text-[10px] tracking-widest mt-2 flex items-center gap-2">
              <Sparkles size={12} className="text-indigo-500" />
              {t('track_and_manage_desc', 'Track and manage your lesson applications')}
            </p>
          </header>
          
          <div className="grid gap-4 md:gap-6">
            {jobs.length === 0 ? (
              <div className="text-center py-20 bg-slate-900/30 rounded-[2rem] border-2 border-dashed border-slate-800 flex flex-col items-center gap-4">
                 <Clock size={32} className="text-slate-700" />
                 <p className="text-slate-600 font-black uppercase tracking-widest text-[10px] md:text-xs">{t('no_active_requests_found', 'No active requests found.')}</p>
                 <button 
                  onClick={() => navigate('/student/post-job')}
                  className="text-indigo-500 font-black uppercase text-[10px] hover:underline"
                 >
                   {t('create_new_request', 'Create a new request')}
                 </button>
              </div>
            ) : (
              jobs.map(job => (
                <div key={job.id} className="bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-[1.5rem] md:rounded-[2.5rem] p-5 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 shadow-2xl hover:border-indigo-500/30 transition-all group active:scale-[0.98]">
                  
                  <div className="flex-1 space-y-3 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-[8px] md:text-[10px] font-black px-3 py-1 rounded-lg uppercase tracking-wider ${
                        job.status === 'ACCEPTED' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                        }`}>
                        {job.status}
                        </span>
                        {job.activeRoomId && (
                            <span className="bg-indigo-500/10 text-indigo-400 text-[8px] md:text-[10px] font-black px-3 py-1 rounded-lg uppercase border border-indigo-500/20 animate-pulse flex items-center gap-1.5">
                                <div className="w-1 h-1 rounded-full bg-indigo-400" /> {t('live_now', 'Live Now')}
                            </span>
                        )}
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-black tracking-tight text-slate-100 group-hover:text-white transition-colors truncate">
                        {job.subject}
                      </h3>
                      <p className="text-slate-500 text-[11px] md:text-sm font-medium leading-relaxed mt-1 line-clamp-2">
                        {job.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between md:flex-col md:items-end gap-4 pt-5 md:pt-0 border-t md:border-t-0 border-slate-800/50 shrink-0">
                    <div className="text-left md:text-right">
                      <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest mb-0.5">{t('budget', 'Budget')}</p>
                      <div className="text-xl md:text-3xl font-black text-white">
                        {job.budget} <span className="text-[10px] md:text-xs text-indigo-500 ml-0.5">ETB</span>
                      </div>
                    </div>
                    
                    {job.status === 'ACCEPTED' && job.activeRoomId ? (
                      <button 
                        onClick={() => window.location.href = `/student/dashboard?room=${job.activeRoomId}`}
                        className="bg-indigo-600 px-6 md:px-10 py-3 md:py-4 rounded-xl md:rounded-2xl text-[9px] md:text-[11px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-indigo-500 shadow-xl shadow-indigo-900/30 transition-all active:scale-95 text-white"
                      >
                        <Video size={16} /> <span className="md:inline">{t('enter_class', 'Enter Class')}</span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 text-slate-600 font-black uppercase italic text-[8px] md:text-[10px] tracking-widest bg-slate-950 px-3 py-2 rounded-lg border border-slate-800">
                        <Clock size={12} /> {job.status === 'ACCEPTED' ? t('in_sync', 'In Sync...') : t('pending', 'Pending')}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <button onClick={() => navigate('/student/home')} className="fixed bottom-6 right-6 bg-indigo-600 hover:bg-indigo-500 text-white p-4 rounded-full shadow-2xl shadow-indigo-500/30 transition-all hover:-translate-y-1 z-50 group flex items-center gap-0 overflow-hidden">
        <Home size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-3 transition-all duration-300 ease-in-out whitespace-nowrap font-bold text-sm uppercase tracking-wider">{t('dashboard', 'Dashboard')}</span>
      </button>
    </div>
  );
};

export default MyJobs;